// Purpose: route guard for the authenticated AppShell. Renders a
// minimal loading state while /api/me resolves, redirects to /login
// on unauthenticated, renders the outlet for signed-in users. Pair
// with <RequireAdmin> inside: this gate ensures user exists, that
// one ensures user.role === 'ADMIN'.
//
// Commit 5.6a adds this. The prior layout rendered AppShell +
// Landing without any auth check — an unauthenticated visitor saw
// the workspace chrome with broken API calls in the background.

import { Navigate, Outlet } from 'react-router-dom';
import { PadlockWatermark, RadialGradientBg } from '../components/atmosphere/index.ts';
import { useAuth } from './AuthContext.tsx';

export function RequireAuth() {
  const { status } = useAuth();
  if (status === 'loading') return <AuthLoadingScreen />;
  if (status === 'unauthenticated') return <Navigate to="/login" replace />;
  return <Outlet />;
}

// Minimal atmospheric holding screen while /api/me resolves. Uses
// the same padlock+gradient vocabulary as the /login page so the
// transition from "loading" to "login redirect" (on 401) doesn't
// flash a generic spinner in between.
function AuthLoadingScreen() {
  return (
    <div className="relative h-screen w-screen overflow-hidden bg-bg-primary text-fg-primary">
      <div aria-hidden className="absolute inset-0 z-0">
        <RadialGradientBg />
        <PadlockWatermark />
      </div>
      <div
        className="absolute inset-0 z-10 flex items-center justify-center"
        role="status"
        aria-live="polite"
      >
        <span className="text-label uppercase text-fg-muted tracking-widest">
          verifying session
        </span>
      </div>
    </div>
  );
}
